/** Simulation tuning. All values are in world units and seconds. */

export interface DifficultyRamp {
  readonly spawnIntervalStart: number;
  readonly spawnIntervalEnd: number;
  readonly maxConcurrentStart: number;
  readonly maxConcurrentEnd: number;
}

export interface GameplayConfig {
  /** Fixed simulation step. */
  readonly fixedStepSeconds: number;
  /** Largest frame delta that is fed to the accumulator. */
  readonly maxFrameSeconds: number;
  /** Difficulty interpolates over this many seconds. */
  readonly rampSeconds: number;
  readonly difficulty: DifficultyRamp;
  /** Seconds an inbound aircraft is telegraphed before it appears. */
  readonly spawnWarningSeconds: number;
  /** Candidate spawns evaluated before the least-bad one is taken. */
  readonly spawnAttempts: number;
  /** Look-ahead horizon used when scoring a candidate spawn. */
  readonly spawnLookaheadSeconds: number;
  /** Separation multiplier a candidate spawn must respect. */
  readonly spawnSeparationFactor: number;
  /** Distance a spawn starts outside the view edge. */
  readonly spawnOffset: number;
  /** Slack beyond the view before an aircraft without a target is culled. */
  readonly despawnMargin: number;
  /**
   * Backstop for an arrival that never reaches the view. Normally an arrival
   * is immune until it has been in shot once; this caps the wait if the view
   * moves out from under it.
   */
  readonly arrivalGraceSeconds: number;
  /** Aircraft younger than this cannot crash. */
  readonly collisionGraceSeconds: number;
  /** Proximity warning radius as a multiple of the summed body radii. */
  readonly warningFactor: number;
  readonly taxiInSpeed: number;
  readonly taxiOutSpeed: number;
  readonly departureSpeed: number;
  /** Minimum turnaround before a landed aircraft may be scheduled out. */
  readonly departureTurnaroundSeconds: number;
  /** Gap enforced between two departures on the same runway. */
  readonly departureDispatchSeconds: number;
  /** Retry delay when a departure would conflict with another. */
  readonly departureRetrySeconds: number;
  /** Take-off progress past which a departure counts as airborne traffic. */
  readonly departureAirborneProgress: number;
  /** Separation multiplier used by the departure conflict test. */
  readonly departureConflictFactor: number;
  /** Distance to the approach gate at which a landing is committed. */
  readonly landingCaptureDistance: number;
  /** How close a drawn path must end to a runway for it to register. */
  readonly runwaySnapDistance: number;
  /** Pointer pick radius for aircraft (mouse). */
  readonly aircraftPickRadius: number;
  /** Pointer pick radius for an existing path (mouse). */
  readonly pathPickRadius: number;
  /** Minimum spacing between sampled drag points. */
  readonly dragSampleDistance: number;
  /** Spacing used when a released path is simplified. */
  readonly pathSimplifyDistance: number;
}

export const GAMEPLAY: GameplayConfig = Object.freeze({
  fixedStepSeconds: 1 / 60,
  maxFrameSeconds: 0.25,
  rampSeconds: 300,
  difficulty: Object.freeze({
    spawnIntervalStart: 5.2,
    spawnIntervalEnd: 1.4,
    maxConcurrentStart: 3,
    maxConcurrentEnd: 12,
  }),
  spawnWarningSeconds: 2.4,
  spawnAttempts: 5,
  spawnLookaheadSeconds: 10,
  spawnSeparationFactor: 1.35,
  spawnOffset: 24,
  despawnMargin: 12,
  arrivalGraceSeconds: 25,
  collisionGraceSeconds: 0.8,
  warningFactor: 3.2,
  taxiInSpeed: 2.25,
  taxiOutSpeed: 3.4,
  departureSpeed: 10.5,
  departureTurnaroundSeconds: 5.5,
  departureDispatchSeconds: 8,
  departureRetrySeconds: 0.5,
  departureAirborneProgress: 0.58,
  departureConflictFactor: 1.12,
  landingCaptureDistance: 0.5,
  runwaySnapDistance: 10,
  aircraftPickRadius: 6,
  pathPickRadius: 4.6,
  dragSampleDistance: 1.4,
  pathSimplifyDistance: 4.2,
});

/**
 * Points.
 *
 * A landing is worth more when it was harder to earn, and the three things
 * that make it harder are all already in the simulation: how rare the airframe
 * is, how much traffic was airborne at the moment of touchdown, and how long a
 * run of clean landings it continued. Nothing here is a bonus for hurrying —
 * the prompt-handling award decays over more than a minute, which rewards not
 * leaving aircraft circling rather than rushing them.
 *
 * `maxPointsPerFlight` is the ceiling all of this can reach, and the edge
 * function re-checks a submitted score against it. Raising any multiplier
 * means raising that too, and redeploying the function.
 */
export const SCORING = Object.freeze({
  landing: 100,
  departure: 40,
  /** Streak multiplier: `1 + min(streak - 1, streakCap) * streakStep`. */
  streakStep: 0.1,
  streakCap: 10,
  /** Traffic multiplier: `1 + min(airborne, trafficCap) * trafficStep`. */
  trafficStep: 0.06,
  trafficCap: 10,
  /**
   * Rarity multiplier: `1 + (1 - weight / commonestWeight) * rarityBonus`, so
   * the fleet weights in `Airframes.ts` are the only place rarity is stated.
   */
  rarityBonus: 0.6,
  /** Paid in full up to `promptSeconds`, gone by `lazySeconds`. */
  promptBonus: 40,
  promptSeconds: 25,
  lazySeconds: 90,
  /** Taken off the score when separation is lost, but never below zero. */
  nearMissPenalty: 42,
  /** Plausibility ceiling, shared with `supabase/functions/leaderboard`. */
  maxPointsPerFlight: 600,
});

/** Orthographic framing: how many world units span the short screen axis. */
export const CAMERA = Object.freeze({
  mobileShortUnits: 118,
  desktopShortUnits: 150,
  desktopMinWidth: 900,
  /** Camera offset from its target, rotated around Z by the orbit angle. */
  offset: Object.freeze({ x: 82, y: -98, z: 92 }),
  rotationStepDegrees: 10,
  /** Multiplier one zoom step applies; the buttons and the wheel share it. */
  zoomStep: 1.2,
  /**
   * Zoom limits. Culling happens `despawnMargin` world units outside the
   * bounds, and the bounds are the quad the view covers at zoom 1 — so a
   * zoom-out wide enough to show past that margin would show aircraft
   * vanishing inside the frame. On desktop that margin is 16% of the half
   * view, so 0.87 (a 15% wider frame) sits just inside it — see the limit
   * check in `render-geometry.test.ts`.
   */
  zoomMin: 0.87,
  zoomMax: 2.4,
  /** Follow time of the zoom ease, in seconds. */
  zoomSmoothing: 0.18,
  /**
   * The "overload breath": how far the view eases out under full traffic.
   *
   * A lens like the player's own zoom, and floored at `zoomMin` for the same
   * reason — the frame must never show past the cull margin. It is deliberately
   * tiny: the point is that a busy sky feels wider, not that the player notices
   * a camera move.
   */
  loadZoom: 0.955,
  /** Airborne count at which the breath is fully out. */
  loadZoomTraffic: 9,
  /** Follow time of the breath, in seconds. Slower than a player zoom. */
  loadZoomSmoothing: 1.8,
  /** Critically damped follow time for the orbit spring. */
  orbitSmoothing: 0.4,
  crashZoom: 1.65,
  crashZoomMs: 820,
  crashHoldMs: 450,
  crashDriftDelay: 1.2,
  crashDriftRate: 0.075,
  returnHomeMs: 900,
  /** CSS pixels per rendered pixel — the source of the chunky pixel look. */
  desktopPixelScale: 2.4,
  mobilePixelScale: 1.85,
});

/**
 * Feel and feedback.
 *
 * Everything here is presentation: the dust, the floating labels, the crash
 * replay and the wind. None of it is read by `src/simulation/` or by
 * `GameSession.step`, so changing a number here cannot change an outcome.
 */
export const FEEDBACK = Object.freeze({
  /** Seconds of the run kept in the replay buffer. */
  replaySeconds: 3,
  /** Poses stored per second; the playback interpolates between them. */
  replaySampleHz: 30,
  /** Playback speed of the crash replay. 1 is real time. */
  replayRate: 0.45,
  /**
   * The last stretch of footage crawls: the impact is the point of the replay,
   * so the closer it gets the slower it runs.
   */
  replayTailSeconds: 0.5,
  /** Multiplier applied to the rate at the moment of impact. */
  replayTailRate: 0.3,
  /** White frame on the touch, in milliseconds. */
  impactFlashMs: 260,
  /** Touchdown dust. */
  dustSeconds: 0.9,
  dustParticles: 8,
  /** How far the furthest particle drifts from the wheels, in world units. */
  dustSpread: 4.4,
  dustSize: 1.15,
  /**
   * Half-angle of the cone a directed puff is thrown into.
   *
   * A touchdown gets the full ring; a take-off gets this cone behind the
   * aircraft, which is what makes it read as thrust rather than impact.
   */
  dustConeRadians: 1.05,
  /** Tyre marks left on the runway. */
  skidSeconds: 3.2,
  skidLength: 8,
  skidWidth: 0.42,
  /** Gap between the two tracks, across the roll direction. */
  skidGauge: 1.6,
  /** Speed lines behind a departure, from this take-off progress on. */
  streakFromProgress: 0.6,
  streakLength: 3.6,
  /** How far an aircraft holding for a slot rocks fore and aft, in units. */
  holdingSway: 0.34,
  /** Apron beacon: blink rate in radians per second, and its duty threshold. */
  beaconRate: 4.2,
  /** Higher is a shorter flash; 0.62 is roughly a fifth of the cycle. */
  beaconDuty: 0.62,
  /** Lifetime of a floating score label. */
  popupMs: 1100,
  /** A streak shorter than this is not worth a flash when it breaks. */
  streakFlashMinimum: 2,
  /** Colour flash and screen shake after a streak breaks. */
  streakFlashMs: 420,
  /** From this streak on, the landing label carries the run of clean landings. */
  streakLabelMinimum: 3,
  /** Ambient wind level over an empty sky, and over a busy one. */
  windQuietLevel: 0.16,
  windBusyLevel: 0.05,
  /** Airborne count at which the wind has stepped back to its busy level. */
  windBusyTraffic: 6,
  /**
   * How tight the proximity ring draws in at the worst separation.
   *
   * The ring used to be binary; scaling it between 1 and this is what turns it
   * into a distance readout the player can act on.
   */
  warningRingTightest: 0.6,
  /** Inbound telegraph: how far the chip stays from the edge of the canvas. */
  telegraphMargin: 34,
  /** Window the frame counter averages over before the readout updates. */
  fpsSampleMs: 500,
  /** At or below this rate the readout is red; above it, green. */
  fpsCriticalFps: 40,
});

/** Dashed path pattern, in world units. */
export const PATH_DASH = Object.freeze({
  length: 3.6,
  gap: 2.4,
  departureLength: 6.2,
});
